import { useState, useEffect, useCallback } from 'react';
import { useCompare, useDeepCompare, useDeepMemo } from '../../../hooks/useCompare';
import { DATA_TABLE_LIMIT, getDataTablePageCount } from '../utils/getDataTableCount';
import { filterElementsByManyKeys } from '../utils/search';
import { getLocalSearchDataWithSubRows } from '../utils/getLocalSearchDataWithSubRows';

interface UseLocalDataTableDataProps<T> {
  networkData: T[];
  /*! Use this variable if the BE does not support pagination
   * and you want still need to paginate the received data.
   * This option is highly recommended for more then 30 items
   */
  setLocalPagination?: boolean;
  /*! Use this variable if BE does not support searching
   * and you still need search.
   */
  setSearch?: { localSearchKeys: (keyof T)[]; value?: string; searchInSubRows?: boolean };
}

interface UseLocalDataTableData<T> {
  allData: T[];
  /*! Remember. This will remove data only from the local state.
   * Ensure that data was removed from the server!
   * If you cannot, you should re-fetch table data.
   */
  removeRows: (uid: string[]) => void;
  setLocalSearch: (uid: string) => void;
  searchValue: string | null;
  onlySearchedData: T[] | null;
  localPagination: {
    totalCount: number;
    pageCount: number;
    pageIndex: number;
    paginatedData: T[] | null;
    setPageIndex: (pageIndex: number) => void;
  };
}

const getTotalCount = <T>(allData: T[], searchedData: T[] | null): number =>
  searchedData ? searchedData.length : allData.length;

const getPaginatedData = <T>(data: T[], pageIndex: number): T[] =>
  data?.slice(
    pageIndex === 0 ? pageIndex : DATA_TABLE_LIMIT * pageIndex,
    pageIndex === 0 ? DATA_TABLE_LIMIT : DATA_TABLE_LIMIT * (pageIndex + 1)
  );

export const useLocalDataTableData = <T extends { uid: string; subRows?: T[] | null }>({
  networkData,
  setLocalPagination = false,
  setSearch
}: UseLocalDataTableDataProps<T>): UseLocalDataTableData<T> => {
  const [allData, setAllData] = useState<T[]>([]);

  const [searchedData, setSearchedData] = useState<T[] | null>(setSearch?.localSearchKeys ? [] : null);
  const [localSearch, setLocalSearch] = useState<string | null>(setSearch?.localSearchKeys ? '' : null);

  const [pageIndex, setPageIndex] = useState<number | null>(setLocalPagination ? 0 : null);
  const [paginatedData, setPaginatedData] = useState<T[] | null>(setLocalPagination ? [] : null);
  const networkDataUidsHasChanged = useDeepCompare(networkData.map((entity) => entity.uid).join());
  const networkDataHasChanged = useDeepCompare(networkData);
  const searchHasChanged = useCompare(localSearch);
  const allDataHasChanged = useDeepCompare(allData);
  const allDataUidsHasChanged = useDeepCompare(allData.map((entity) => entity.uid).join());

  useEffect(() => {
    if (networkDataUidsHasChanged) {
      setPageIndex(0);
    }
    if (networkDataHasChanged) {
      setAllData(networkData);
      if (setSearch?.localSearchKeys) {
        setSearchedData(networkData);
      }
    }
  }, [networkData, networkDataHasChanged, networkDataUidsHasChanged, setSearch?.localSearchKeys]);

  useEffect(() => {
    if (typeof setSearch?.value === 'string') {
      setPageIndex(0);
      setLocalSearch(setSearch.value);
    }
  }, [setSearch?.value]);

  useEffect(() => {
    // For optimization verify if allData or search value have changed.
    // Check if user has set search props
    if (allDataUidsHasChanged && localSearch !== null) {
      const pageCount = getDataTablePageCount(undefined, allData.length, DATA_TABLE_LIMIT) - 1;
      if (typeof pageIndex === 'number' && pageCount < pageIndex) {
        setPageIndex(pageCount < 0 ? 0 : pageCount);
      }
    }
    if ((searchHasChanged || allDataHasChanged) && localSearch !== null && setSearch?.localSearchKeys) {
      if (!setSearch.searchInSubRows) {
        setSearchedData(filterElementsByManyKeys(localSearch, allData || [], setSearch.localSearchKeys));
      } else {
        setSearchedData(getLocalSearchDataWithSubRows(localSearch, allData || [], setSearch.localSearchKeys));
      }
    }
  }, [localSearch, setSearch, searchHasChanged, allDataUidsHasChanged, allData, allDataHasChanged, pageIndex]);

  useEffect(() => {
    if (setLocalPagination) {
      setPaginatedData(getPaginatedData(searchedData || allData, pageIndex!));
    }
  }, [searchedData, pageIndex, setLocalPagination, allData]);

  const totalCount = getTotalCount(allData, searchedData);
  const pageCount = getDataTablePageCount(undefined, totalCount, DATA_TABLE_LIMIT);

  const removeRows = useCallback(
    (uids: string[]) => setAllData((prevState) => prevState.filter((data) => !uids.includes(data.uid))),
    []
  );
  const searchHandler = useCallback((search: string) => setLocalSearch(search), []);

  return useDeepMemo(
    (): UseLocalDataTableData<T> => ({
      allData,
      onlySearchedData: searchedData,
      removeRows,
      setLocalSearch: searchHandler,
      searchValue: localSearch,
      localPagination: {
        totalCount,
        pageCount,
        setPageIndex,
        paginatedData,
        pageIndex: pageIndex ?? 0
      }
    }),
    [allData, localSearch, pageCount, pageIndex, paginatedData, removeRows, searchHandler, searchedData, totalCount]
  );
};
